import React from 'react';
import { shallowEqual } from 'react-redux';

import { RouteContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { HomePageContent } from 'src/legacy/components/HomePage';
import { NotAllowedPage } from 'src/legacy/components/UI/NotAllowedPage';
import { LoadingPage } from 'src/legacy/components/UI/Pages/LoadingPage';
import { MfaEnforcementPage } from 'src/legacy/components/UI/Pages/MfaEnforcementPage';
import { NetworkErrorPage } from 'src/legacy/components/UI/Pages/NetworkErrorPage';
import type { RouteAccess } from 'src/routes/routeAccess';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';
import { ensureApiError } from 'src/utils/Errors';

export interface AccessGateProps {
  access?: keyof RouteAccess;
  unauthAccess?: boolean;
  children: React.ReactNode;
}

export const AccessGate: React.FC<AccessGateProps> = ({
  access,
  unauthAccess = false,
  children,
}) => {
  const { isLoading: isPortalConfigurationLoading, error } =
    useGetPortalConfigQuery();
  const portalConfigError = ensureApiError(error);

  const { routeAccess, isLoading: isAccessLoading } = useRouteAccess();

  const { loadingError, permissions, userLoaded } = useAppSelector(
    (state: RootState) => ({
      loadingError: state.user.loadingError,
      permissions: state.user.data?.permissions,
      userLoaded: state.user.loaded,
    }),
    shallowEqual,
  );

  const { pathname } = React.useContext(RouteContext);
  const isClientUser = useAppSelector((state) => state.user.isClient);

  const allowed = access ? routeAccess[access] : undefined;

  const redirectToHomePage =
    (pathname === '/' || pathname.startsWith('/portal/')) && !isClientUser;

  // Priority 1: MFA enforcement
  if (portalConfigError.code === 'mfa_required') {
    return <MfaEnforcementPage />;
  }

  // Priority 2: Network/loading error
  if (loadingError) {
    return <NetworkErrorPage />;
  }

  // Priority 3: Access not allowed (only for authenticated users with loaded permissions)
  if (!allowed && !isAccessLoading && userLoaded && access !== undefined) {
    return <NotAllowedPage />;
  }

  // Priority 4: Loading states
  if (
    isPortalConfigurationLoading ||
    isAccessLoading ||
    (!permissions && !unauthAccess)
  ) {
    return <LoadingPage />;
  }

  // Priority 5: Homepage redirect for non-client users
  if (redirectToHomePage) {
    return <HomePageContent />;
  }

  return <>{children}</>;
};
